/**
 * Terms of Service — user-facing, warm, plain English.
 *
 * Starter document. Before public launch, have counsel review and swap
 * jurisdiction/arbitration/dispute-resolution clauses appropriate for
 * Rammy's home base + user geography.
 */
import LegalLayout from "@/pages/LegalLayout";

export default function Terms() {
  return (
    <LegalLayout title="Terms of Service" updated="February 2026" testid="page-terms">
      <p>
        These terms govern your use of Rammy — the web app at rammy.ai and
        the eventual desktop hardware companion. By creating an account or
        using the free hero chat, you agree to them. If you don't agree, don't
        use the service. Simple as that.
      </p>

      <h2>1. Who can use Rammy</h2>
      <p>
        You must be at least 13 years old to create an account. If you are
        under the age of majority in your jurisdiction, a parent or legal
        guardian must agree to these terms on your behalf.
      </p>

      <h2>2. Your account</h2>
      <ul>
        <li>You are responsible for keeping your password secure. Don't share it.</li>
        <li>One human per account. Sharing an account with another person violates these terms.</li>
        <li>You are responsible for content you send to Rammy and any actions you take through connected integrations (sending an email through Gmail, creating a calendar event, etc.).</li>
      </ul>

      <h2>3. What Rammy will (and won't) do</h2>
      <p>
        Rammy is designed to be honest — when it doesn't know something, it
        will say so. But Rammy is still an AI system built on top of third-party
        LLMs and can occasionally produce inaccurate, incomplete, or
        surprising output. <strong>Don't rely on Rammy for medical, legal,
        financial, or safety-critical decisions.</strong> Treat its answers as
        a knowledgeable friend's, not as professional advice.
      </p>

      <h2>4. Acceptable use</h2>
      <p>You agree NOT to use Rammy to:</p>
      <ul>
        <li>Break the law or help others break the law.</li>
        <li>Impersonate another person, including cloning a voice you do not have permission to clone. The "My Voice" enrollment requires explicit two-checkbox consent and is bound to the account owner.</li>
        <li>Generate content that harasses, threatens, defames, or dehumanises a person or group.</li>
        <li>Generate sexual content involving minors, or any other content prohibited by our AI providers' policies (OpenAI, Anthropic, Google, ElevenLabs).</li>
        <li>Send spam or bulk-unsolicited messages through connected accounts.</li>
        <li>Reverse-engineer, scrape, or attempt to bypass any technical or licensing controls in the service.</li>
      </ul>
      <p>
        We may suspend or terminate accounts that violate these rules. When
        we can, we'll tell you why.
      </p>

      <h2>5. Free tier + paid plans</h2>
      <p>
        The free tier gives you basic chat, voice, memory, image understanding,
        and live fact-checking with fair-use daily limits. Paid plans (Plus,
        Pro) unlock higher usage caps, all six voice personalities, and early
        feature access. Prices, features, and limits are listed on the pricing
        page and may change with reasonable notice.
      </p>
      <p>
        Payments are processed by Stripe. Subscriptions renew automatically
        until you cancel. You can cancel any time from Settings — you will
        keep paid access until the end of your current billing period, then
        auto-revert to the free tier.
      </p>

      <h2>6. Refunds</h2>
      <p>
        If you're unhappy in your first 14 days on a paid plan, email us at
        <a href="mailto:hello@rammy.ai"> hello@rammy.ai</a> and we'll
        refund the current month. Beyond that window we do not offer prorated
        refunds; use your remaining paid time and cancel before renewal.
      </p>

      <h2>7. Your content, your ownership</h2>
      <p>
        Everything you send to Rammy — messages, images, memories, generated
        artefacts — remains yours. You grant Rammy a limited, worldwide licence
        to store, process, and display that content solely for the purpose of
        running the service for you. That licence ends the moment you delete
        the content or your account.
      </p>
      <p>
        Anything Rammy generates for you (text, images, audio, video) is yours
        to use, subject to the terms of the underlying AI providers (OpenAI's,
        Anthropic's, Google's, ElevenLabs' generation-content policies).
      </p>

      <h2>8. Beta status</h2>
      <p>
        Rammy is currently in prototype/beta. Features may change, break, or
        disappear. We'll do our best to communicate significant changes, but
        by using a beta product you accept a higher baseline of change than a
        mature service would show.
      </p>

      <h2>9. Availability + limits of liability</h2>
      <p>
        The service is provided "as is." We do not guarantee uninterrupted
        availability, absence of bugs, or fitness for any particular purpose.
        To the fullest extent allowed by law, Rammy, its owners, and its
        employees are not liable for indirect, incidental, or consequential
        damages arising from your use of the service.
      </p>

      <h2>10. Termination</h2>
      <p>
        You can delete your account at any time from Settings. We can
        terminate accounts that materially violate these terms or that pose a
        safety or legal risk to other users. On termination, your data is
        deleted per the Privacy policy.
      </p>

      <h2>11. Governing law</h2>
      <p>
        These terms are governed by the laws of the jurisdiction in which
        Rammy's operating entity is registered, without regard to conflict-of-
        law rules. Where local consumer-protection law grants you stronger
        rights, those rights are not overridden by this section.
      </p>

      <h2>12. Contact</h2>
      <p>
        Questions about these terms → <a href="mailto:legal@rammy.ai">legal@rammy.ai</a>.
        Anything else → <a href="mailto:hello@rammy.ai">hello@rammy.ai</a>.
      </p>
    </LegalLayout>
  );
}
